/**
 * EmergenceOverlay Component
 *
 * Displays a floating pixel animation overlay for progressive creature loading.
 * Shows the creature sprite (when available) with silver/cyan pixels converging
 * and a progress percentage display.
 *
 * Used in:
 * - Generation page (during creature creation)
 * - Gallery (for in-progress creatures)
 * - Animation Studio (for incomplete creatures)
 */

import { useMemo } from 'react';
import styles from './EmergenceOverlay.module.css';

export interface EmergenceOverlayProps {
  /** Base64 or URL of sprite image (undefined if not yet available) */
  sprite?: string;
  /** Progress percentage (0-100) */
  progress: number;
  /** Status message to display (e.g., "Generating animations...") */
  statusMessage?: string;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Whether generation is complete (triggers fade-out of overlay) */
  isComplete?: boolean;
  /** Additional CSS class */
  className?: string;
}

/**
 * Generate pixel elements for the floating animation
 */
function FloatingPixels({ count = 12 }: { count?: number }) {
  const pixels = useMemo(() => {
    return Array.from({ length: count }, (_, i) => (
      <div key={i} className={styles.pixel} />
    ));
  }, [count]);

  return <div className={styles.pixelsContainer}>{pixels}</div>;
}

/**
 * EmergenceOverlay Component
 *
 * Progressive loading overlay with floating pixels and progress display.
 */
export function EmergenceOverlay({
  sprite,
  progress,
  statusMessage,
  size = 'medium',
  isComplete = false,
  className = '',
}: EmergenceOverlayProps) {
  // Determine if we should show the sprite or placeholder
  const showSprite = Boolean(sprite);

  // Clamp progress to 0-100
  const clampedProgress = Math.min(100, Math.max(0, progress));

  // Build container class
  const containerClass = [
    styles.container,
    styles[size],
    isComplete ? styles.complete : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClass}>
      {/* Sprite or placeholder */}
      <div className={styles.spriteContainer}>
        {showSprite ? (
          <img
            src={sprite!.startsWith('data:') ? sprite : `data:image/png;base64,${sprite}`}
            alt="Generating creature"
            className={`${styles.sprite} ${styles.fadeIn}`}
          />
        ) : (
          <div className={styles.placeholder}>
            <span>Generating...</span>
          </div>
        )}
      </div>

      {/* Floating pixels overlay */}
      {!isComplete && <FloatingPixels count={12} />}

      {/* Progress display */}
      {!isComplete && (
        <div className={styles.progressContainer}>
          <span className={styles.progressPercentage}>{clampedProgress}%</span>
          {statusMessage && (
            <span className={styles.statusMessage}>{statusMessage}</span>
          )}
        </div>
      )}

      {/* Progress bar at bottom */}
      {!isComplete && (
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${clampedProgress}%` }}
          />
        </div>
      )}
    </div>
  );
}

export default EmergenceOverlay;
